import React, { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import api from "@/lib/api";
import { BRAND } from "@/constants/brand";
import { MapPin, Phone, Mail, Clock, Send, ShieldAlert } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      const res = await api.post("/contact", { ...payload, name: form.name, email: form.email, message: form.message });
      toast.success(res.data?.message || "Thank you!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <PageHero title="Contact Us" subtitle="We typically respond within 48 hours. For urgent matters, please call us directly." />

      <Section>
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <div className="hc-card p-6">
              <div className="font-bold text-lg text-[#0F2A4A]">{BRAND.parent}</div>
              <div className="text-xs text-slate-500">LLPIN: {BRAND.llpin}</div>
              <ul className="mt-4 space-y-3 text-sm text-slate-700">
                <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#3780E0] shrink-0" /> {BRAND.address}</li>
                <li className="flex gap-2"><Phone className="w-4 h-4 mt-0.5 text-[#3780E0] shrink-0" /> <a href={`tel:${BRAND.phone}`} className="hc-link">{BRAND.phone}</a></li>
                <li className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 text-[#3780E0] shrink-0" /> <a href={`mailto:${BRAND.email}`} className="hc-link">{BRAND.email}</a></li>
                <li className="flex gap-2"><Clock className="w-4 h-4 mt-0.5 text-[#3780E0] shrink-0" /> Mon – Sat, 10:00 AM – 6:00 PM (IST)</li>
              </ul>
            </div>

            <div className="hc-card p-6 bg-[#E8F0FB]">
              <div className="flex items-center gap-2 text-[#2563B3]">
                <ShieldAlert className="w-5 h-5" /> <span className="font-bold">Grievance Officer</span>
              </div>
              <div className="mt-3 text-sm text-[#0F2A4A]">
                <div className="font-semibold">{BRAND.grievanceOfficer}</div>
                <div>{BRAND.parent} — HomeCure</div>
                <div className="mt-2 space-y-1">
                  <div><span className="text-slate-600">Email: </span><a href={`mailto:${BRAND.email}`} className="hc-link">{BRAND.email}</a></div>
                  <div><span className="text-slate-600">Phone: </span><a href={`tel:${BRAND.phone}`} className="hc-link">{BRAND.phone}</a></div>
                  <div><span className="text-slate-600">Hours: </span>Mon – Sat, 10 AM – 6 PM (IST)</div>
                </div>
                <p className="mt-3 text-xs text-slate-700">
                  As per Rule 5(9) of the SPDI Rules, 2011, Section 13 of the DPDP Act, 2023, and IT Intermediary Guidelines, 2021. Acknowledgement within 48 hours, resolution within 30 days.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 hc-card p-6 md:p-8" data-testid="contact-form">
            <h2 className="text-2xl font-bold text-[#0F2A4A]">Send us a message</h2>
            <p className="text-sm text-slate-600 mt-1">For partnership inquiries, please use the <a className="hc-link" href="/partner">Partner with us</a> page.</p>
            <div className="mt-5 grid md:grid-cols-2 gap-4">
              <div><label className="hc-label">Name *</label><input required value={form.name} onChange={set("name")} className="hc-input" data-testid="contact-name" /></div>
              <div><label className="hc-label">Email *</label><input required type="email" value={form.email} onChange={set("email")} className="hc-input" data-testid="contact-email" /></div>
              <div><label className="hc-label">Phone</label><input value={form.phone} onChange={set("phone")} className="hc-input" /></div>
              <div><label className="hc-label">Subject</label><input value={form.subject} onChange={set("subject")} className="hc-input" /></div>
            </div>
            <div className="mt-4"><label className="hc-label">Message *</label><textarea required rows={5} value={form.message} onChange={set("message")} className="hc-input" data-testid="contact-message" /></div>
            <button type="submit" disabled={submitting} className="hc-btn-primary mt-5 disabled:opacity-60" data-testid="contact-submit">
              <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </Section>

      <Section bg="light" eyebrow="Find us" title="Registered office — Asansol, West Bengal" center>
        <div className="hc-card p-0 overflow-hidden">
          <iframe
            title="HomeCure office"
            src="https://www.google.com/maps?q=Hirapur,Asansol,West+Bengal,India&output=embed"
            width="100%" height="380"
            style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </Section>
    </>
  );
}
